import {
  CallListType,
  NodeType,
  useGetCallContactListLazyQuery,
  useGetConsultantCallListDataLazyQuery
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { AddCircleOutline } from "@material-ui/icons";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { CallList } from "./call-list";
import { EditListModal } from "./components/edit-list-modal";
import { NewListModal } from "./components/new-list-modal";
import { useConsultantJoinCallList } from "./hooks/consultant-join-call-list";
import { useConsultantLeaveCallList } from "./hooks/consultant-leave-call-list";
import { CallOrContactList } from "./utils";

type Props = {
  open: boolean;
  consultantId: string;
  onClose: () => void;
};

type Option = {
  label: string;
  value: CallOrContactList;
};

export function CallListModal(props: Props) {
  const classes = useStyles();
  const [consultantCallLists, setConsultantCallLists] = useState<CallOrContactList[]>([]);
  const [allCallLists, setAllCallLists] = useState<CallOrContactList[]>([]);

  const [addListType, setAddListType] = useState<CallListType | null>(null);
  const [editListId, setEditListId] = useState<string | null>(null);

  const snack = useSnackbarsPipeline();

  const [fetchConsultantData, consultantDataQuery] =
    useGetConsultantCallListDataLazyQuery({
      variables: {
        id: props.consultantId
      },
      fetchPolicy: "cache-first"
    });

  const [fetchLists] = useGetCallContactListLazyQuery({
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setAllCallLists([...data.me.CallLists, ...data.me.ContactLists]);

      if (props.consultantId) {
        const items = data.me.CallLists.concat(data.me.ContactLists).filter((list) =>
          list.nodes.some((node) => node.NodeID === props.consultantId)
        );

        setConsultantCallLists(items);
      }
    }
  });

  useEffect(() => {
    if (props.open) {
      setConsultantCallLists([]);
      setAllCallLists([]);
      fetchLists();
      fetchConsultantData();
    }
  }, [props.open, fetchLists]);

  const onOptionEdit = (option: Option) => {
    setEditListId(option.value._id);
  };

  const onCompleted = () => {
    snack.showSuccess(locale.Saved);
  };

  const onJoinError = (e) => {
    console.error(e);
    snack.showError(
      locale.addError(locale.Consultant.toLowerCase(), locale.CallList.toLowerCase())
    );
  };

  const onConsultantLeaveError = (e) => {
    console.error(e);
    snack.showError(
      locale.removeError(locale.Consultant.toLowerCase(), locale.CallList.toLowerCase())
    );
  };

  const { consultantJoinCallList } = useConsultantJoinCallList({
    onCompleted,
    onError: onJoinError
  });

  const { consultantLeaveCallList } = useConsultantLeaveCallList({
    onCompleted,
    onError: onConsultantLeaveError
  });

  const onChange = (value: CallOrContactList[]) => {
    const joinedCallList = value.find(
      (callList) =>
        !consultantCallLists.some(
          (originalCallList) => originalCallList._id === callList._id
        )
    );

    const leftCallList = consultantCallLists.find(
      (originalCallList) =>
        !value.some((selectedCallList) => selectedCallList._id === originalCallList._id)
    );

    joinedCallList &&
      (consultantDataQuery.data?.consultantById.Unfit
        ? snack.showError(locale.UnfitConsultantError)
        : consultantJoinCallList({
            id: joinedCallList._id,
            node: {
              NodeID: props.consultantId,
              NodeType: NodeType.Consultant,
              Name: consultantDataQuery?.data?.consultantById.Name ?? ""
            },
            affectedCallList: joinedCallList
          }));

    leftCallList &&
      consultantLeaveCallList({
        id: leftCallList._id,
        nodeId: props.consultantId,
        affectedCallList: leftCallList
      });
  };

  const editItem = editListId
    ? allCallLists.find((list) => list._id === editListId)
    : null;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>{locale.callAndContactLists}</DialogTitle>

      <DialogContent classes={{ root: classes.dialogContent }}>
        <CallList
          value={consultantCallLists}
          onChange={onChange}
          onOptionEdit={onOptionEdit}
        />

        <EditListModal
          open={!!editItem}
          onClose={() => setEditListId(null)}
          callList={editItem}
          type={editItem?.__typename as CallListType}
        />

        <NewListModal
          open={!!addListType}
          consultantId={props.consultantId}
          onClose={() => setAddListType(null)}
          type={addListType}
        />
      </DialogContent>

      <DialogActions style={{ padding: "16px 24px" }}>
        <Button
          startIcon={<AddCircleOutline />}
          color="primary"
          variant="outlined"
          onClick={() => setAddListType(CallListType.CallList)}
        >
          {locale.NewCallList}
        </Button>

        <Button
          startIcon={<AddCircleOutline />}
          color="primary"
          style={{ marginRight: "auto" }}
          variant="outlined"
          onClick={() => setAddListType(CallListType.ContactList)}
        >
          {locale.NewContactList}
        </Button>

        <Button variant="outlined" onClick={props.onClose}>
          {locale.Close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles({
  dialogPaper: {
    minHeight: 600,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column"
  },
  dialogContent: {
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    flex: 1
  }
});
